import React, { useEffect, useRef, useState } from "react";
import { useMemberstack } from "@memberstack/react";
import { API_BASE } from "../../config";
import { useNavigate } from "react-router-dom";
import { AlertMessage } from "../../components/MessageAlert";
import { Input } from "../../components/catalyst/input";
import { Field, Label, Description } from "../../components/catalyst/fieldset";
import AddressInput from "../../components/AdressInput";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";

function OnBoardFicheNotaire() {
  const memberstack = useMemberstack();
  const navigate = useNavigate();

  const [member, setMember] = useState(null);

  // ── Mode : rejoindre une étude existante ou en créer une nouvelle ────────────
  const [mode, setMode] = useState(null); // null | 'existing' | 'new'

  // ── Étude existante ──────────────────────────────────────────────────────────
  const [etudes, setEtudes]               = useState([]);
  const [etudesLoading, setEtudesLoading] = useState(false);
  const [etudeSearch, setEtudeSearch]     = useState("");
  const [selectedEtudeId, setSelectedEtudeId] = useState(null);
  const [dropdownOpen, setDropdownOpen]   = useState(false);
  const dropdownRef = useRef(null);

  // ── Nouvelle étude ────────────────────────────────────────────────────────────
  const [nomEtude, setNomEtude]             = useState("");
  const [site, setSite]                     = useState("");
  const [siret, setSiret]                   = useState("");
  const [telephone, setTelephone]           = useState("");
  const [presentation, setPresentation]     = useState("");
  const [selectedAddress, setSelectedAddress]   = useState(null);
  const [selectedAddress2, setSelectedAddress2] = useState(null);
  const [deuxiemeAdresse, setDeuxiemeAdresse]   = useState(false);

  // ── UI ────────────────────────────────────────────────────────────────────────
  const [erreurZapier, setErreurZapier] = useState(false);
  const [messageErreur, setMessageErreur] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Récupération du membre
  useEffect(() => {
    memberstack
      .getCurrentMember()
      .then(({ data }) => {
        if (!data) { navigate("/app/connexion", { replace: true }); return; }
        setMember(data);
      })
      .catch(console.error);
  }, [memberstack, navigate]);

  // Chargement des études pour le dropdown
  useEffect(() => {
    if (mode !== 'existing') return;
    setEtudesLoading(true);
    fetch(`${API_BASE}/api/etudes/list`)
      .then(r => r.json())
      .then(d => setEtudes(d.etudes || []))
      .catch(() => setEtudes([]))
      .finally(() => setEtudesLoading(false));
  }, [mode]);

  // Fermer le dropdown en cliquant dehors
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredEtudes = etudes.filter(e =>
    e.nom.toLowerCase().includes(etudeSearch.toLowerCase()) ||
    e.adresse.toLowerCase().includes(etudeSearch.toLowerCase())
  );

  const selectedEtude = etudes.find(e => e.id === selectedEtudeId) || null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting || !mode) return;

    if (!member) {
      setErreurZapier(true);
      setMessageErreur("Session expirée. Merci de vous reconnecter.");
      return;
    }

    if (mode === 'existing' && !selectedEtudeId) {
      setErreurZapier(true);
      setMessageErreur("Veuillez sélectionner une étude dans la liste.");
      return;
    }

    if (mode === 'new') {
      if (!selectedAddress?.formatted_address) {
        setErreurZapier(true);
        setMessageErreur("Veuillez sélectionner une adresse dans la liste Google.");
        return;
      }
      if (deuxiemeAdresse && !selectedAddress2?.formatted_address) {
        setErreurZapier(true);
        setMessageErreur("Veuillez sélectionner la deuxième adresse dans la liste Google.");
        return;
      }
    }

    try {
      setIsSubmitting(true);
      setErreurZapier(false);

      const body = mode === 'existing'
        ? { member, etudeExistanteId: selectedEtudeId }
        : {
            member, nomEtude, siret, telephone, site, selectedAddress, presentation,
            ...(deuxiemeAdresse ? { selectedAddress2 } : {}),
          };

      const response = await fetch(`${API_BASE}/api/newNotaire1`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Erreur serveur");

      navigate("/app/onboard-notaire/fiche", { replace: true });
    } catch (error) {
      console.error("erreur newNotaire1 :", error);
      setErreurZapier(true);
      setMessageErreur(error.message || "Oops il semblerait qu'il y ait eu un problème, veuillez recommencer");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-full place-items-center bg-gradient-to-b from-indigo-100 to-pink-100 px-6 py-24 sm:py-24 lg:px-8">
      <div className="text-left mb-5">
        <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          Bienvenue dans votre espace notaire
        </h1>
        <p className="mt-1 text-sm leading-6 text-gray-600">
          Nous avons besoin de quelques informations pour paramétrer votre compte
        </p>
      </div>

      <div className="space-y-10 divide-y divide-gray-900/10">
        <div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
          <div className="px-4 sm:px-0">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Informations au sujet de votre étude
            </h2>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2"
          >
            {erreurZapier && <AlertMessage type="error" message={messageErreur} />}

            <div className="px-4 py-6 sm:p-8 space-y-6">

              {/* ── Sélection du mode ────────────────────────────────────────── */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Votre étude est-elle déjà présente sur Clotere ?
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    {
                      value: 'existing',
                      label: 'Oui, rejoindre mon étude',
                      desc: 'Votre étude existe déjà, vous souhaitez la rejoindre',
                    },
                    {
                      value: 'new',
                      label: 'Non, créer mon étude',
                      desc: 'Vous êtes le premier notaire de votre étude sur Clotere',
                    },
                  ].map(({ value, label, desc }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => { setMode(value); setErreurZapier(false); }}
                      className={`flex flex-col items-start rounded-xl border-2 p-4 text-left transition-all
                        ${mode === value
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-gray-50'}`}
                    >
                      <p className={`text-sm font-semibold ${mode === value ? 'text-indigo-700' : 'text-gray-900'}`}>
                        {label}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Mode : étude existante ───────────────────────────────────── */}
              {mode === 'existing' && (
                <div>
                  <Field>
                    <Label>Rechercher votre étude <sup>*</sup></Label>
                    <Description className="text-sm text-gray-500">
                      Tapez le nom ou la ville de votre étude
                    </Description>
                  </Field>

                  <div className="relative mt-2" ref={dropdownRef}>
                    <input
                      type="text"
                      value={selectedEtude ? selectedEtude.nom : etudeSearch}
                      onChange={(e) => {
                        setEtudeSearch(e.target.value);
                        setSelectedEtudeId(null);
                        setDropdownOpen(true);
                      }}
                      onFocus={() => setDropdownOpen(true)}
                      placeholder={etudesLoading ? "Chargement…" : "Ex : Étude Dupont, Paris…"}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />

                    {dropdownOpen && !selectedEtude && (
                      <div className="absolute z-20 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg max-h-60 overflow-y-auto">
                        {etudesLoading ? (
                          <div className="px-4 py-3 text-sm text-gray-400">Chargement…</div>
                        ) : filteredEtudes.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-400">
                            Aucune étude trouvée. Créez la vôtre ci-dessus.
                          </div>
                        ) : (
                          filteredEtudes.map(etude => (
                            <button
                              key={etude.id}
                              type="button"
                              onClick={() => {
                                setSelectedEtudeId(etude.id);
                                setEtudeSearch(etude.nom);
                                setDropdownOpen(false);
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-0"
                            >
                              <p className="text-sm font-semibold text-gray-900">{etude.nom}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{etude.adresse}</p>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {selectedEtude && (
                    <div className="mt-3 flex items-start justify-between rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
                      <div>
                        <p className="text-sm font-semibold text-indigo-900">{selectedEtude.nom}</p>
                        <p className="text-xs text-indigo-600 mt-0.5">{selectedEtude.adresse}</p>
                        {selectedEtude.siret && (
                          <p className="text-xs text-gray-500 mt-0.5">SIRET : {selectedEtude.siret}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => { setSelectedEtudeId(null); setEtudeSearch(""); }}
                        className="ml-3 text-indigo-400 hover:text-indigo-700"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ── Mode : nouvelle étude ────────────────────────────────────── */}
              {mode === 'new' && (
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                  <div className="sm:col-span-4">
                    <Field>
                      <Label>Nom de l'étude <sup>*</sup></Label>
                      <Input value={nomEtude} onChange={(e) => setNomEtude(e.target.value)} required />
                    </Field>
                  </div>

                  <div className="sm:col-span-4">
                    <Field>
                      <Label>SIRET <sup>*</sup></Label>
                      <Input value={siret} onChange={(e) => setSiret(e.target.value)} required />
                    </Field>
                  </div>

                  <div className="sm:col-span-4">
                    <Field>
                      <Label>Téléphone <sup>*</sup></Label>
                      <Description className="text-sm leading-6 text-gray-600">
                        Le numéro sur lequel vos clients peuvent vous joindre
                      </Description>
                      <Input value={telephone} onChange={(e) => setTelephone(e.target.value)} required />
                    </Field>
                  </div>

                  <div className="sm:col-span-4">
                    <Field>
                      <Label>Site internet</Label>
                      <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 sm:max-w-md">
                        <span className="flex select-none items-center pl-3 text-gray-500 sm:text-sm">
                          https://
                        </span>
                        <Input value={site} onChange={(e) => setSite(e.target.value)} />
                      </div>
                    </Field>
                  </div>

                  <div className="sm:col-span-6">
                    <Field>
                      <Label>Adresse <sup>*</sup></Label>
                      <Description className="text-sm leading-6 text-gray-600">
                        Adresse de l'étude pour les rendez-vous physiques.
                      </Description>
                      <AddressInput onAddressSelect={setSelectedAddress} placeholderText="Adresse principale" />
                    </Field>

                    {deuxiemeAdresse && (
                      <Field className="mt-3">
                        <Label>Seconde adresse <sup>*</sup></Label>
                        <AddressInput onAddressSelect={setSelectedAddress2} placeholderText="Seconde adresse" />
                      </Field>
                    )}

                    {deuxiemeAdresse ? (
                      <button
                        type="button"
                        className="flex flex-row gap-2 items-center underline mt-2 mb-2"
                        onClick={() => { setDeuxiemeAdresse(false); setSelectedAddress2(null); }}
                      >
                        <XMarkIcon className="size-6" /> Annuler
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="flex flex-row gap-2 items-center underline mt-2 mb-2"
                        onClick={() => setDeuxiemeAdresse(true)}
                      >
                        <PlusIcon className="size-6" /> Ajouter une deuxième adresse
                      </button>
                    )}
                  </div>

                  <div className="sm:col-span-6">
                    <Field>
                      <Label>Présentation</Label>
                      <textarea
                        value={presentation}
                        onChange={(e) => setPresentation(e.target.value)}
                        rows={6}
                        className="w-full rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-indigo-500 outline-none"
                        placeholder="Présentez votre étude, vos spécialités, votre approche..."
                      />
                    </Field>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
              <button
                type="submit"
                disabled={isSubmitting || !mode}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
              >
                {isSubmitting ? "Enregistrement..." : "Continuer"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export default OnBoardFicheNotaire;
